<template>
  <div class="student-submission-list">
    <div class="page-header">
      <div class="header-content">
        <h2 class="page-title">
          <i class="el-icon-document"></i>
          学生提交列表 - {{ homeworkName }}
        </h2>
        <div class="header-actions">
          <el-button @click="goBack" icon="el-icon-arrow-left">返回</el-button>
          <el-button type="primary" @click="refreshData" icon="el-icon-refresh">刷新</el-button>
        </div>
      </div>
    </div>

    <div class="page-content">
      <!-- 作业信息卡片 -->
      <el-card class="homework-info-card" shadow="never">
        <div slot="header" class="card-header">
          <span class="card-title">作业信息</span>
        </div>
        <div class="homework-info">
          <div class="info-item">
            <span class="label">作业名称：</span>
            <span class="value">{{ homeworkName }}</span>
          </div>
          <div class="info-item">
            <span class="label">班级：</span>
            <span class="value">{{ className }}</span>
          </div>
          <div class="info-item">
            <span class="label">总学生数：</span>
            <span class="value">{{ totalStudents }}</span>
          </div>
          <div class="info-item">
            <span class="label">未提交：</span>
            <span class="value not-submitted-count">{{ notSubmittedCount }}</span>
          </div>
          <div class="info-item">
            <span class="label">未批改：</span>
            <span class="value not-graded-count">{{ notGradedCount }}</span>
          </div>
          <div class="info-item">
            <span class="label">已批改：</span>
            <span class="value graded-count">{{ gradedCount }}</span>
          </div>
        </div>
        <div class="sorting-info" style="margin-top: 15px; padding: 10px; background: #f0f9ff; border-radius: 6px; border-left: 4px solid #0ea5e9;">
          <div style="font-size: 13px; color: #0369a1; display: flex; align-items: center;">
            <i class="el-icon-info" style="margin-right: 6px;"></i>
            <span><strong>排序规则：</strong>未提交 → 未批改 → 已批改（同状态内按学号排序）</span>
          </div>
        </div>
      </el-card>

      <!-- 学生列表 -->
      <el-card class="student-list-card" shadow="never">
        <div slot="header" class="card-header">
          <span class="card-title">学生列表
            <el-tag size="mini" type="info" style="margin-left: 8px;">
              <i class="el-icon-sort"></i>
              按批改状态排序
            </el-tag>
          </span>
          <div class="header-filters">
            <el-select v-model="statusFilter" placeholder="筛选状态" clearable @change="filterStudents" style="width: 120px;">
              <el-option label="全部" value=""></el-option>
              <el-option label="未提交" value="not-submitted"></el-option>
              <el-option label="未批改" value="not-graded"></el-option>
              <el-option label="已批改" value="graded"></el-option>
            </el-select>
            <el-input
              v-model="searchKeyword"
              placeholder="搜索学生姓名或学号"
              prefix-icon="el-icon-search"
              @input="filterStudents"
              style="width: 200px; margin-left: 10px;"
            />
          </div>
        </div>

        <el-table
          :data="filteredStudents"
          border
          style="width: 100%"
          v-loading="loading"
          element-loading-text="加载中..."
        >
          <el-table-column prop="student_num" label="学号" width="120" align="center" />
          <el-table-column prop="name" label="学生姓名" width="120" align="center">
            <template slot-scope="scope">
              <span class="student-name" @click="showCorrectionResult(scope.row)">
                {{ scope.row.name }}
              </span>
            </template>
          </el-table-column>
          <el-table-column label="状态" width="120" align="center">
            <template slot-scope="scope">
              <el-tag :type="getSubmissionStatusSync(scope.row).type" size="small">
                {{ getSubmissionStatusSync(scope.row).text }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="提交时间" width="180" align="center">
            <template slot-scope="scope">
              <span v-if="getSubmissionStatusSync(scope.row).text !== '未提交'">
                {{ getSubmissionTime(scope.row) }}
              </span>
              <span v-else style="color: #999;">-</span>
            </template>
          </el-table-column>
          <el-table-column label="得分" width="100" align="center">
            <template slot-scope="scope">
              <span v-if="getSubmissionStatusSync(scope.row).text === '已批改'">
                {{ getStudentScore(scope.row) }}
              </span>
              <span v-else style="color: #999;">-</span>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="200" align="center">
            <template slot-scope="scope">
              <el-button
                v-if="getSubmissionStatusSync(scope.row).text !== '未提交'"
                type="primary"
                size="mini"
                @click="showCorrectionResult(scope.row)"
                icon="el-icon-edit"
              >
                批改
              </el-button>
              <el-button
                v-if="getSubmissionStatusSync(scope.row).text !== '未提交'"
                type="warning"
                size="mini"
                @click="showReuploadDialog(scope.row)"
                icon="el-icon-upload2"
              >
                重新上传
              </el-button>
              <el-button
                v-if="getSubmissionStatusSync(scope.row).text === '未提交'"
                type="success"
                size="mini"
                @click="showUploadDialog(scope.row)"
                icon="el-icon-upload"
              >
                上传作业
              </el-button>
              <el-button
                v-if="getSubmissionStatusSync(scope.row).text === '未提交'"
                type="info"
                size="mini"
                disabled
                icon="el-icon-view"
              >
                未提交
              </el-button>
            </template>
          </el-table-column>
        </el-table>

        <!-- 分页 -->
        <div class="pagination-container" v-if="totalPages > 1">
          <el-pagination
            @current-change="handlePageChange"
            :current-page="currentPage"
            :page-size="pageSize"
            :total="totalStudents"
            layout="total, prev, pager, next, jumper"
            background
          />
        </div>
      </el-card>
    </div>

    <!-- 批改结果弹窗 -->
    <el-dialog title="作业批改结果" :visible.sync="correctionResultVisible" width="80%">
      <!-- 调试信息显示 -->
      <div class="debug-info" style="background: #f0f9ff; border: 1px solid #0ea5e9; border-radius: 6px; padding: 12px; margin-bottom: 16px;">
        <h4 style="margin: 0 0 8px 0; color: #0369a1; font-size: 14px;">🔍 调试信息</h4>
        <div style="display: flex; gap: 20px; font-size: 13px; color: #0c4a6e;">
          <div><strong>学生ID:</strong> {{ currentStudent ? currentStudent.id : 'N/A' }}</div>
          <div><strong>学号:</strong> {{ currentStudent ? currentStudent.student_num : 'N/A' }}</div>
          <div><strong>学生姓名:</strong> {{ currentStudent ? currentStudent.name : 'N/A' }}</div>
          <div><strong>作业ID:</strong> {{ currentHomework ? currentHomework.id : 'N/A' }}</div>
          <div><strong>作业名称:</strong> {{ currentHomework ? currentHomework.name : 'N/A' }}</div>
        </div>
        <div style="margin-top: 8px; font-size: 12px; color: #64748b;">
          <div><strong>查询条件:</strong> student_id = {{ currentStudent ? currentStudent.id : 'N/A' }}, homework_id = {{ currentHomework ? currentHomework.id : 'N/A' }}</div>
          <div><strong>提交记录数:</strong> {{ studentSubmissions ? studentSubmissions.length : 0 }}</div>
          <div><strong>批改结果数:</strong> {{ correctionResults ? correctionResults.length : 0 }}</div>
          <div><strong>当前图片URL:</strong> {{ studentImageUrl || '无图片' }}</div>
          <div><strong>提交记录详情:</strong> {{ studentSubmissions.length > 0 ? JSON.stringify(studentSubmissions[0], null, 2) : '无提交记录' }}</div>
        </div>
      </div>
      <div class="correction-dialog-body-beauty">
        <div class="correction-image-beauty">
          <img
            v-if="studentImageUrl"
            :src="studentImageUrl"
            alt="作业图片"
            :style="{
              maxWidth: '100%',
              maxHeight: '700px',
              borderRadius: '12px',
              boxShadow: '0 2px 12px rgba(0,0,0,0.12)',
              background: '#fff',
              padding: '12px',
              cursor: dragging ? 'grabbing' : 'grab',
              transform: `scale(${imageScale}) translate(${imageOffset.x}px, ${imageOffset.y}px)`,
              transition: dragging ? 'none' : 'transform 0.2s',
              position: dragging ? 'absolute' : 'static',
              zIndex: dragging ? 10 : 'auto',
              left: 0,
              top: 0
            }"
            @wheel.prevent="handleImageWheel"
            @mousedown="startDrag"
            @load="onImageLoad"
            @error="onImageError"
          />
          <div v-else class="no-image">暂无图片</div>
          <div class="zoom-tip">滚轮缩放，拖拽图片</div>
          <!-- 添加图片切换控制 -->
          <div class="image-navigation" v-if="studentSubmissions.length > 1">
            <el-button
              :disabled="currentImageIndex === 0"
              @click="prevImage"
              icon="el-icon-arrow-left"
            >上一张</el-button>
            <span class="image-counter">{{ currentImageIndex + 1 }}/{{ studentSubmissions.length }}</span>
            <el-button
              :disabled="currentImageIndex === studentSubmissions.length - 1"
              @click="nextImage"
              icon="el-icon-arrow-right"
            >下一张</el-button>
          </div>
        </div>
        <div class="correction-table-beauty">
          <div class="student-info" v-if="currentStudent">
            <h3>学生：{{ currentStudent.username }} <span v-if="currentStudentScore !== null">- 得分: {{ currentStudentScore }}</span></h3>
          </div>
          <el-table :data="correctionResults" border style="width: 100%">
            <el-table-column label="序号" type="index" width="60" align="center" />
            <el-table-column prop="stem" label="题目" min-width="300">
              <template slot-scope="scope">
                <div v-html="renderMathFormula(scope.row.stem)"></div>
              </template>
            </el-table-column>
            <el-table-column label="学生答案" min-width="200">
              <template slot-scope="scope">
                <div class="student-answer">
                  <!-- 图片答案 -->
                  <div v-if="scope.row.answerImageUrl" class="answer-image">
                    <el-button
                      type="primary"
                      size="mini"
                      icon="el-icon-view"
                      @click="previewImage(scope.row.answerImageUrl)"
                    >
                      查看图片
                    </el-button>
                  </div>
                  <!-- 文字答案 -->
                  <div v-if="scope.row.answerText" class="answer-text">
                    <div v-html="renderMathFormula(scope.row.answerText)"></div>
                  </div>
                  <!-- 无答案 -->
                  <div v-if="!scope.row.answerText && !scope.row.answerImageUrl" class="no-answer">
                    <span style="color: #999;">未作答</span>
                  </div>
                </div>
              </template>
            </el-table-column>
            <el-table-column prop="point" label="分数" width="100" align="center">
              <template slot-scope="scope">
                <el-input v-model="scope.row.point" size="small" placeholder="请输入分数" type="number" min="0" style="width: 80px;" />
              </template>
            </el-table-column>
            <el-table-column prop="judge" label="结果" width="150" align="center">
              <template slot-scope="scope">
                <el-dropdown @command="val => handleJudgeChange(scope.$index, val)">
                  <el-tag
                    :type="scope.row.judge === '正确' ? 'success' : (scope.row.judge === '错误' ? 'danger' : 'info')"
                    style="cursor:pointer"
                  >
                    {{ scope.row.judge || '未批改' }}
                    <i class="el-icon-arrow-down el-icon--right"></i>
                  </el-tag>
                  <el-dropdown-menu slot="dropdown">
                    <el-dropdown-item command="正确">
                      <el-tag type="success">正确</el-tag>
                    </el-dropdown-item>
                    <el-dropdown-item command="错误">
                      <el-tag type="danger">错误</el-tag>
                    </el-dropdown-item>
                    <el-dropdown-item command="未批改">
                      <el-tag type="info">未批改</el-tag>
                    </el-dropdown-item>
                  </el-dropdown-menu>
                </el-dropdown>
              </template>
            </el-table-column>
            <el-table-column prop="comment" label="评语" min-width="200">
              <template slot-scope="scope">
                <el-input v-model="scope.row.comment" placeholder="请输入评语" size="small" />
              </template>
            </el-table-column>
          </el-table>

          <!-- 整体作业评语 -->
          <div class="homework-comment-section" style="margin-top: 20px; padding: 20px; background: #f8f9fa; border-radius: 8px; border: 1px solid #e9ecef;">
            <h4 style="margin: 0 0 15px 0; color: #495057; font-size: 16px;">
              <i class="el-icon-edit" style="margin-right: 8px;"></i>
              整体作业评语
            </h4>
            <el-input
              v-model="homeworkComment"
              type="textarea"
              :rows="4"
              placeholder="请输入对整体作业的评价和建议..."
              maxlength="500"
              show-word-limit
              style="width: 100%;"
            />
          </div>
        </div>
      </div>
      <span slot="footer" class="dialog-footer" style="display: flex; flex-direction: column; align-items: center;">
        <div style="margin-bottom: 15px;">
          <el-checkbox v-model="autoAddToWrongBook" style="margin-right: 20px;">
            自动将错误题目添加到学生错题本
          </el-checkbox>
          <span v-if="autoAddToWrongBook && errorQuestionCount > 0" style="color: #E6A23C; font-size: 12px;">
            (将添加 {{ errorQuestionCount }} 道错误题目)
          </span>
        </div>
        <div>
          <el-button :disabled="currentPage === 1" @click="prevPage">上一页</el-button>
          <span style="margin: 0 15px;">第{{ currentPage }}/{{ totalPages }}页</span>
          <el-button :disabled="currentPage === totalPages" @click="nextPage">下一页</el-button>
        </div>
        <div style="margin-bottom: 20px;">
          <el-button @click="correctionResultVisible = false">关 闭</el-button>
          <el-button type="primary" @click="saveCorrectionResults">保存</el-button>
        </div>
      </span>
    </el-dialog>

    <!-- 上传作业图片对话框 -->
    <el-dialog
      title="上传作业图片"
      :visible.sync="uploadDialogVisible"
      width="500px"
      :before-close="closeUploadDialog"
    >
      <div class="upload-dialog-content">
        <div class="student-info">
          <h4>{{ currentUploadStudent.name }} ({{ currentUploadStudent.student_num }})</h4>
          <p>作业：{{ homeworkName }}</p>
        </div>

        <el-upload
          class="upload-demo"
          drag
          :action="uploadAction"
          :headers="uploadHeaders"
          :data="uploadData"
          :on-success="handleUploadSuccess"
          :on-error="handleUploadError"
          :before-upload="beforeUpload"
          :file-list="uploadFileList"
          :limit="1"
          accept="image/*"
        >
          <i class="el-icon-upload"></i>
          <div class="el-upload__text">将作业图片拖到此处，或<em>点击上传</em></div>
          <div class="el-upload__tip" slot="tip">只能上传jpg/png文件，且不超过10MB</div>
        </el-upload>
      </div>

      <div slot="footer" class="dialog-footer">
        <el-button @click="closeUploadDialog">取消</el-button>
        <el-button type="primary" @click="confirmUpload" :loading="uploading">确认上传</el-button>
      </div>
    </el-dialog>

    <!-- 重新上传作业图片对话框 -->
    <el-dialog
      title="重新上传作业图片"
      :visible.sync="reuploadDialogVisible"
      width="500px"
      :before-close="closeReuploadDialog"
    >
      <div class="reupload-dialog-content">
        <div class="student-info">
          <h4>{{ currentReuploadStudent.name }} ({{ currentReuploadStudent.student_num }})</h4>
          <p>作业：{{ homeworkName }}</p>
        </div>

        <!-- 显示当前图片 -->
        <div class="current-image" v-if="currentSubmissionImage">
          <h5>当前作业图片：</h5>
          <img :src="currentSubmissionImage" alt="当前作业图片" style="max-width: 100%; max-height: 200px; border: 1px solid #ddd; border-radius: 4px;">
        </div>

        <el-upload
          class="upload-demo"
          drag
          :action="reuploadAction"
          :headers="uploadHeaders"
          :data="reuploadData"
          :on-success="handleReuploadSuccess"
          :on-error="handleReuploadError"
          :before-upload="beforeUpload"
          :file-list="reuploadFileList"
          :limit="1"
          accept="image/*"
        >
          <i class="el-icon-upload"></i>
          <div class="el-upload__text">将新的作业图片拖到此处，或<em>点击上传</em></div>
          <div class="el-upload__tip" slot="tip">只能上传jpg/png文件，且不超过10MB</div>
        </el-upload>
      </div>

      <div slot="footer" class="dialog-footer">
        <el-button @click="closeReuploadDialog">取消</el-button>
        <el-button type="primary" @click="confirmReupload" :loading="reuploading">确认重新上传</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getSubmitByStudentIdAndHomeworkId, getSubmitDetails, gradeHomework, uploadHomeworkImage, reuploadHomeworkImage } from '@/api/homeworkSubmit'
import { getSpecificHomeworkQuestions } from '@/api/homework'
import { getStudentsByClassId } from '@/api/grade'

export default {
  name: 'StudentSubmissionList',
  data() {
    return {
      // 页面参数
      homeworkId: null,
      classId: null,
      homeworkName: '',
      className: '',

      // 学生数据
      allStudents: [],
      filteredStudents: [],
      submissions: [],
      studentStatuses: {}, // 存储学生的状态信息

      // 筛选和分页
      statusFilter: '',
      searchKeyword: '',
      currentPage: 1,
      pageSize: 20,

      // 加载状态
      loading: false,

      // 批改相关
      correctionResultVisible: false,
      correctionResults: [],
      currentStudent: null,
      currentHomework: null,
      studentSubmissions: [],
      currentImageIndex: 0,
      studentImageUrl: '',
      homeworkComment: '',
      autoAddToWrongBook: false,

      // 图片操作
      imageScale: 1,
      imageOffset: { x: 0, y: 0 },
      dragging: false,
      dragStart: { x: 0, y: 0 },
      lastOffset: { x: 0, y: 0 },

      // 分页相关
      totalPages: 1,

      // 上传相关数据
      uploadDialogVisible: false,
      reuploadDialogVisible: false,
      currentUploadStudent: {},
      currentReuploadStudent: {},
      currentSubmissionImage: '',
      uploading: false,
      reuploading: false,
      uploadFileList: [],
      reuploadFileList: [],
      uploadAction: '/api/homework-submit/upload-image',
      reuploadAction: '/api/homework-submit/reupload-image',
      uploadHeaders: {
        'Authorization': 'Bearer ' + (localStorage.getItem('token') || '')
      }
    }
  },
  computed: {
    totalStudents() {
      return this.allStudents.length
    },
    submittedCount() {
      return this.submissions.filter(s => s.submitted).length
    },
    notSubmittedCount() {
      return Object.values(this.studentStatuses).filter(status => status.text === '未提交').length
    },
    notGradedCount() {
      return Object.values(this.studentStatuses).filter(status => status.text === '未批改').length
    },
    gradedCount() {
      return Object.values(this.studentStatuses).filter(status => status.text === '已批改').length
    },
    errorQuestionCount() {
      return this.correctionResults.filter(r => r.judge === '错误').length
    },

    // 上传相关计算属性
    uploadData() {
      return {
        homeworkId: this.homeworkId,
        studentId: this.currentUploadStudent.id
      }
    },

    reuploadData() {
      return {
        homeworkId: this.homeworkId,
        studentId: this.currentReuploadStudent.id
      }
    }
  },
  created() {
    this.initPage()
  },
  methods: {
    // 初始化页面
    initPage() {
      const { homeworkId, classId, homeworkName, className } = this.$route.query
      this.homeworkId = parseInt(homeworkId)
      this.classId = parseInt(classId)
      this.homeworkName = homeworkName || '未知作业'
      this.className = className || '未知班级'

      this.currentHomework = {
        id: this.homeworkId,
        name: this.homeworkName
      }

      this.loadData()
    },

    // 加载数据
    async loadData() {
      this.loading = true
      try {
        await Promise.all([
          this.loadStudents(),
          this.loadSubmissions()
        ])
        this.filterStudents()
      } catch (error) {
        this.$message.error('加载数据失败')
        console.error(error)
      } finally {
        this.loading = false
      }
    },

    // 加载学生列表
    async loadStudents() {
      try {
        const response = await getStudentsByClassId(this.classId)
        if (response.code === 1 && response.data) {
          this.allStudents = response.data
        }
      } catch (error) {
        console.error('加载学生列表失败:', error)
      }
    },

    // 加载提交记录
    async loadSubmissions() {
      try {
        // 为每个学生加载提交记录和状态信息
        this.submissions = []
        this.studentStatuses = {}

        for (const student of this.allStudents) {
          try {
            const response = await getSubmitByStudentIdAndHomeworkId(student.id, this.homeworkId)
            if (response.code === 1 && response.data) {
              // 添加提交记录
              this.submissions.push({
                studentId: student.id,
                submitted: true,
                submitTime: response.data.submitTime,
                imageUrl: response.data.answerImageUrl,
                score: response.data.totalScore,
                status: response.data.status
              })

              // 检查批改状态
              try {
                const detailsResponse = await getSubmitDetails(response.data.submitId)
                if (detailsResponse.code === 1 && detailsResponse.data && detailsResponse.data.length > 0) {
                  // 有批改详情，检查是否有实际的批改内容
                  const hasGrading = detailsResponse.data.some(detail =>
                    detail.isCorrect !== null ||
                    (detail.teacherComment && detail.teacherComment.trim() !== '') ||
                    (detail.studentScore && detail.studentScore > 0)
                  )
                  this.studentStatuses[student.id] = hasGrading
                    ? { type: 'success', text: '已批改' }
                    : { type: 'warning', text: '未批改' }
                } else {
                  // 没有批改详情，说明已提交但未批改
                  this.studentStatuses[student.id] = { type: 'warning', text: '未批改' }
                }
              } catch (error) {
                console.error(`检查学生${student.name}批改状态失败:`, error)
                this.studentStatuses[student.id] = { type: 'info', text: '已提交' }
              }
            } else {
              // 没有提交记录
              this.studentStatuses[student.id] = { type: 'danger', text: '未提交' }
            }
          } catch (error) {
            console.log(`学生${student.name}(${student.id})没有提交记录`)
            this.studentStatuses[student.id] = { type: 'danger', text: '未提交' }
          }
        }
        console.log('加载的提交记录:', this.submissions)
        console.log('加载的学生状态:', this.studentStatuses)
      } catch (error) {
        console.error('加载提交记录失败:', error)
      }
    },

    // 筛选学生
    filterStudents() {
      let filtered = [...this.allStudents]

      // 状态筛选
      if (this.statusFilter) {
        filtered = filtered.filter(student => {
          const status = this.getSubmissionStatusSync(student)
          if (this.statusFilter === 'not-submitted') {
            return status.text === '未提交'
          } else if (this.statusFilter === 'not-graded') {
            return status.text === '未批改'
          } else if (this.statusFilter === 'graded') {
            return status.text === '已批改'
          }
          return true
        })
      }

      // 关键词搜索
      if (this.searchKeyword) {
        const keyword = this.searchKeyword.toLowerCase()
        filtered = filtered.filter(student =>
          student.name.toLowerCase().includes(keyword) ||
          student.student_num.toLowerCase().includes(keyword)
        )
      }

      // 按批改状态排序：未提交 > 未批改 > 已批改
      filtered.sort((a, b) => {
        const statusA = this.getSubmissionStatusSync(a)
        const statusB = this.getSubmissionStatusSync(b)

        // 定义排序优先级：未提交 > 未批改 > 已批改
        const getSortOrder = (status) => {
          switch (status.text) {
            case '未提交': return 0
            case '未批改': return 1
            case '已批改': return 2
            default: return 3
          }
        }

        const orderA = getSortOrder(statusA)
        const orderB = getSortOrder(statusB)

        // 如果状态相同，按学号排序
        if (orderA === orderB) {
          return a.student_num.localeCompare(b.student_num)
        }

        return orderA - orderB
      })

      console.log('学生列表排序完成，排序结果:')
      filtered.forEach((student, index) => {
        const status = this.getSubmissionStatusSync(student)
        console.log(`${index + 1}. ${student.name}(${student.student_num}) - ${status.text}`)
      })

      this.filteredStudents = filtered
    },

    // 获取提交状态（同步方法）
    getSubmissionStatusSync(student) {
      return this.studentStatuses[student.id] || { type: 'danger', text: '未提交' }
    },

    // 获取提交状态（异步方法，用于其他需要实时检查的地方）
    async getSubmissionStatus(student) {
      const submission = this.submissions.find(s => s.studentId === student.id)
      if (!submission) {
        return { type: 'danger', text: '未提交' }
      }

      // 检查是否已批改
      try {
        const submitResponse = await getSubmitByStudentIdAndHomeworkId(student.id, this.homeworkId)
        if (submitResponse.code === 1 && submitResponse.data) {
          // 获取批改详情
          const detailsResponse = await getSubmitDetails(submitResponse.data.submitId)
          if (detailsResponse.code === 1 && detailsResponse.data && detailsResponse.data.length > 0) {
            // 有批改详情，检查是否有实际的批改内容
            const hasGrading = detailsResponse.data.some(detail =>
              detail.isCorrect !== null ||
              (detail.teacherComment && detail.teacherComment.trim() !== '') ||
              (detail.studentScore && detail.studentScore > 0)
            )
            if (hasGrading) {
              return { type: 'success', text: '已批改' }
            } else {
              return { type: 'warning', text: '未批改' }
            }
          } else {
            // 没有批改详情，说明已提交但未批改
            return { type: 'warning', text: '未批改' }
          }
        }
      } catch (error) {
        console.error('检查批改状态失败:', error)
        // 如果检查失败，默认显示为已提交
        return { type: 'info', text: '已提交' }
      }

      return { type: 'info', text: '已提交' }
    },

    // 获取提交时间
    getSubmissionTime(student) {
      const submission = this.submissions.find(s => s.studentId === student.id)
      return submission ? submission.submitTime : '-'
    },

    // 获取学生得分
    getStudentScore(student) {
      const submission = this.submissions.find(s => s.studentId === student.id)
      return submission ? submission.score : '-'
    },

    // 图像加载成功
    onImageLoad(event) {
      console.log('✅ 图像加载成功:', event.target.src)
      console.log('图像尺寸:', event.target.naturalWidth, 'x', event.target.naturalHeight)
    },

    // 图像加载失败
    onImageError(event) {
      console.error('❌ 图像加载失败:', event.target.src)
      console.error('错误详情:', event)
      this.$message.error('图像加载失败: ' + event.target.src)
    },

    // 显示批改结果
    async showCorrectionResult(student) {
      try {
        console.log('=== 开始批改结果查询 ===')
        console.log('showCorrectionResult - 学生数据:', student)
        console.log('showCorrectionResult - 作业数据:', this.currentHomework)
        console.log('🔧 代码版本: 2025-01-27 修复版本')
        console.log('📋 查询目标: t_homework_submit表，字段: answer_image_url')

        const studentIdToUse = student.id
        console.log('使用id字段:', studentIdToUse)

        console.log('查询参数 - student_id:', parseInt(studentIdToUse), 'homework_id:', parseInt(this.currentHomework.id))
        console.log('🔗 API调用URL: /homework-submit/student/' + parseInt(studentIdToUse) + '/homework/' + parseInt(this.currentHomework.id))
        console.log('🧪 测试API调用 - 请在浏览器控制台手动执行以下命令:')
        console.log('fetch("http://localhost:8081/api/homework-submit/student/55/homework/58").then(r => r.json()).then(console.log)')
        let submitResponse
        try {
          console.log('📡 开始调用API...')
          submitResponse = await getSubmitByStudentIdAndHomeworkId(parseInt(studentIdToUse), parseInt(this.currentHomework.id))
          console.log('📡 API调用完成')
          console.log('submitResponse:', submitResponse)
          console.log('submitResponse.code:', submitResponse.code)
          console.log('submitResponse.msg:', submitResponse.msg)
          console.log('submitResponse.data:', submitResponse.data)
          console.log('📸 t_homework_submit表数据:')
          console.log('  - submitId:', submitResponse.data?.submitId)
          console.log('  - studentId:', submitResponse.data?.studentId)
          console.log('  - homeworkId:', submitResponse.data?.homeworkId)
          console.log('  - answerImageUrl:', submitResponse.data?.answerImageUrl)
          console.log('  - submitTime:', submitResponse.data?.submitTime)
          console.log('  - submitStatus:', submitResponse.data?.submitStatus)
          console.log('  - teacherComment:', submitResponse.data?.teacherComment)
          console.log('submitResponse.data完整内容:', JSON.stringify(submitResponse.data, null, 2))
        } catch (error) {
          console.error('获取提交记录失败:', error)
          this.$message.error('获取提交记录失败: ' + error.message)
          return
        }

        console.log('🔍 检查submitResponse条件:')
        console.log('submitResponse.code === 1:', submitResponse.code === 1)
        console.log('submitResponse.data存在:', !!submitResponse.data)
        console.log('submitResponse.data内容:', submitResponse.data)
        console.log('submitResponse.data类型:', typeof submitResponse.data)
        console.log('submitResponse.data是否为null:', submitResponse.data === null)
        console.log('submitResponse.data是否为undefined:', submitResponse.data === undefined)

        if (submitResponse.code === 1 && submitResponse.data) {
          console.log('进入有提交记录的分支')
          // 有提交记录，获取详细信息
          // 加载整体作业评语
          this.homeworkComment = submitResponse.data.teacherComment || ''

          let detailsResponse
          try {
            detailsResponse = await getSubmitDetails(submitResponse.data.submitId)
            console.log('detailsResponse:', detailsResponse)
            console.log('detailsResponse.code:', detailsResponse.code)
            console.log('detailsResponse.data:', detailsResponse.data)
            console.log('detailsResponse.data length:', detailsResponse.data?.length)
          } catch (error) {
            console.error('获取提交详情失败:', error)
            detailsResponse = { code: 0, data: null }
          }

          if (detailsResponse.code === 1 && detailsResponse.data && detailsResponse.data.length > 0) {
            console.log('📝 有详细信息，构建studentSubmissions数组')
            // 构建学生提交记录数组 - 最左边图片使用t_homework_submit中的图片
            this.studentSubmissions = detailsResponse.data.map(detail => ({
              submitId: submitResponse.data.submitId,
              homeworkId: this.currentHomework.id,
              studentId: student.id,
              imageUrl: submitResponse.data.answerImageUrl, // 使用t_homework_submit中的图片
              submitTime: submitResponse.data.submitTime,
              point: detail.studentScore, // 修正字段名
              status: submitResponse.data.status
            }))
            console.log('📝 从detailsResponse构建的studentSubmissions:', this.studentSubmissions)
          } else {
            console.log('📝 没有详细信息，使用主记录构建studentSubmissions')
            // 没有详细信息，使用主记录
            this.studentSubmissions = [{
              submitId: submitResponse.data.submitId,
              homeworkId: this.currentHomework.id,
              studentId: student.id,
              imageUrl: submitResponse.data.answerImageUrl, // 直接使用t_homework_submit中的图片
              submitTime: submitResponse.data.submitTime,
              point: null,
              status: submitResponse.data.status
            }]
            console.log('📝 从主记录构建的studentSubmissions:', this.studentSubmissions)
          }
        } else {
          console.log('❌ 进入没有提交记录的分支')
          console.log('submitResponse.code:', submitResponse.code)
          console.log('submitResponse.data:', submitResponse.data)
          console.log('submitResponse.msg:', submitResponse.msg)
          console.log('🔍 条件检查失败原因:')
          console.log('  - submitResponse.code === 1:', submitResponse.code === 1)
          console.log('  - submitResponse.data存在:', !!submitResponse.data)
          console.log('  - 两个条件都满足:', submitResponse.code === 1 && submitResponse.data)
          // 没有提交记录
          this.studentSubmissions = []
        }

        console.log('studentSubmissions数组:', this.studentSubmissions)
        console.log('studentSubmissions长度:', this.studentSubmissions.length)

        if (this.studentSubmissions.length > 0) {
          this.currentImageIndex = 0
          // 确保图片URL是完整的URL
          let imageUrl = this.studentSubmissions[0].imageUrl
          console.log('studentSubmissions[0]:', this.studentSubmissions[0])
          console.log('原始图片URL:', imageUrl)
          console.log('图片URL类型:', typeof imageUrl)
          console.log('图片URL是否为空:', !imageUrl)
          console.log('图片URL长度:', imageUrl ? imageUrl.length : 0)
          console.log('图片URL是否以http开头:', imageUrl ? imageUrl.startsWith('http') : false)
          console.log('图片URL是否以/question-info/开头:', imageUrl ? imageUrl.startsWith('/question-info/') : false)

          if (imageUrl && !imageUrl.startsWith('http')) {
            // MinIO文件直接通过MinIO API访问
            if (imageUrl.startsWith('/question-info/')) {
              // MinIO文件路径格式：/bucketName/fileName
              imageUrl = `http://localhost:9000${imageUrl}`
              console.log('🔗 构建MinIO URL:', imageUrl)
            } else {
              // 其他文件通过本地文件系统访问
              imageUrl = `http://localhost:8080/api/file/preview?path=${encodeURIComponent(imageUrl)}`
              console.log('🔗 构建本地文件URL:', imageUrl)
            }
            console.log('处理后的图片URL:', imageUrl)
          } else {
            console.log('图片URL已经是完整URL或为空，无需处理')
          }
          this.studentImageUrl = imageUrl
          console.log('最终设置的studentImageUrl:', this.studentImageUrl)
          console.log('🎯 图像显示测试 - 请检查浏览器网络面板中是否有图片请求')
        } else {
          this.studentImageUrl = ''
          console.log('没有提交记录，设置studentImageUrl为空')
        }

        this.currentStudent = student
        this.currentStudentScore = this.studentSubmissions.length > 0 ? this.studentSubmissions[0].point : null

        // 处理不同类型作业的批改结果
        if (this.currentHomework.isImageType) {
          // 图片作业只显示图片，无需加载题目
          this.correctionResults = []
        } else {
          // 普通作业处理逻辑
          // 如果有提交记录，从提交详情中获取批改结果
          if (this.studentSubmissions.length > 0 && submitResponse.code === 1 && submitResponse.data) {
            try {
              const detailsResponse = await getSubmitDetails(submitResponse.data.submitId)
              if (detailsResponse.code === 1 && detailsResponse.data) {
                // 从提交详情构建批改结果
                console.log('原始提交详情数据:', detailsResponse.data)
                this.correctionResults = detailsResponse.data.map(detail => {
                  const result = {
                    id: detail.questionId,
                    questionId: detail.questionId,
                    stem: '', // 需要从题目详情中获取
                    judge: detail.isCorrect === 1 ? '正确' : detail.isCorrect === 0 ? '错误' : '未批改',
                    comment: detail.teacherComment || '',
                    point: detail.score || 0,
                    answerText: detail.answerText || '',
                    answerImageUrl: detail.answerImageUrl || ''
                  }
                  console.log(`题目${detail.questionId}的批改结果:`, result)
                  return result
                })

                // 获取题目详情来填充题目内容
                const questionsRes = await getSpecificHomeworkQuestions(this.currentHomework.id)
                console.log('获取题目详情响应:', questionsRes)
                if (questionsRes && questionsRes.code === 1 && questionsRes.data) {
                  this.correctionResults = this.correctionResults.map(result => {
                    const question = questionsRes.data.find(q => q.questionId === result.questionId)
                    console.log('匹配题目:', {
                      resultQuestionId: result.questionId,
                      question: question,
                      questionContent: question ? question.content : null
                    })
                    return {
                      ...result,
                      stem: question ? (question.content || question.stem || '') : result.stem
                    }
                  })
                  console.log('最终批改结果:', this.correctionResults)
                }
              } else {
                // 没有批改详情，用作业题目详情填充
                await this.loadDefaultCorrectionResults()
              }
            } catch (error) {
              console.error('获取批改详情失败:', error)
              await this.loadDefaultCorrectionResults()
            }
          } else {
            // 没有提交记录，用作业题目详情填充
            await this.loadDefaultCorrectionResults()
          }
        }

        this.correctionResultVisible = true
      } catch (error) {
        this.$message.error('获取批改结果失败')
        console.error(error)
      }
    },

    // 加载默认的批改结果
    async loadDefaultCorrectionResults() {
      try {
        const questionsRes = await getSpecificHomeworkQuestions(this.currentHomework.id)
        console.log('loadDefaultCorrectionResults - 获取题目详情响应:', questionsRes)
        if (questionsRes && questionsRes.code === 1 && questionsRes.data) {
          this.correctionResults = questionsRes.data.map(q => ({
            id: q.questionId,
            questionId: q.questionId,
            stem: q.content || q.stem || '',
            judge: '未批改', // 未批改
            comment: '',
            point: 0, // 新增分数字段
            answerText: '', // 无学生答案
            answerImageUrl: '' // 无学生答案图片
          }))
        } else {
          this.correctionResults = []
        }
      } catch (error) {
        console.error('加载默认批改结果失败:', error)
        this.correctionResults = []
      }
    },

    // 保存批改结果
    async saveCorrectionResults() {
      try {
        console.log('saveCorrectionResults - 当前学生数据:', this.currentStudent)
        console.log('saveCorrectionResults - 当前作业数据:', this.currentHomework)

        const studentId = this.currentStudent && this.currentStudent.id // 使用id字段，不是student_num
        const homeworkId = this.currentHomework && this.currentHomework.id

        console.log('saveCorrectionResults - 学生ID:', studentId)
        console.log('saveCorrectionResults - 学号:', this.currentStudent && this.currentStudent.student_num)
        console.log('saveCorrectionResults - 作业ID:', homeworkId)

        // 获取提交记录ID
        if (!this.studentSubmissions || this.studentSubmissions.length === 0) {
          this.$message.error('未找到学生提交记录')
          return
        }

        const submitId = this.studentSubmissions[0].submitId
        console.log('saveCorrectionResults - 提交ID:', submitId)

        // 构建批改详情数据
        const details = this.correctionResults.map(result => ({
          questionId: result.id || result.questionId,
          studentScore: Number(result.point) || 0,
          teacherComment: result.comment || '',
          isCorrect: result.judge === '正确' ? 1 : (result.judge === '错误' ? 0 : null)
        }))

        console.log('saveCorrectionResults - 批改详情:', details)

        // 使用新的API保存批改结果
        await gradeHomework(submitId, details, this.homeworkComment)

        // 计算总分
        const totalPoint = details.reduce((sum, detail) => sum + (detail.studentScore || 0), 0)
        console.log('saveCorrectionResults - 总分:', totalPoint)

        // 自动添加错误题目到错题本（如果用户选择了该选项）
        if (this.autoAddToWrongBook) {
          const wrongResults = this.correctionResults.filter(r => r.judge === '错误')
          if (wrongResults.length > 0) {
            await this.addWrongQuestionsToUserbook(wrongResults, studentId)
          }
        }

        this.$message.success('批改完成')
        this.correctionResultVisible = false

        // 更新当前学生的状态为已批改
        if (this.currentStudent) {
          this.studentStatuses[this.currentStudent.id] = { type: 'success', text: '已批改' }
        }

        // 刷新数据
        this.refreshData()
      } catch (error) {
        this.$message.error('批改失败')
        console.error(error)
      }
    },

    // 预览图片
    previewImage(imageUrl) {
      if (imageUrl) {
        window.open(imageUrl, '_blank')
      }
    },

    // 处理判断结果变化
    handleJudgeChange(index, val) {
      this.correctionResults[index].judge = val
    },

    // 切换到上一张图片
    prevImage() {
      if (this.currentImageIndex > 0) {
        this.currentImageIndex--
        let imageUrl = this.studentSubmissions[this.currentImageIndex].imageUrl
        if (imageUrl && !imageUrl.startsWith('http')) {
          // MinIO文件直接通过MinIO API访问
          if (imageUrl.startsWith('/question-info/')) {
            // MinIO文件路径格式：/bucketName/fileName
            imageUrl = `http://localhost:9000${imageUrl}`
          } else {
            // 其他文件通过本地文件系统访问
            imageUrl = `http://localhost:8080/api/file/preview?path=${encodeURIComponent(imageUrl)}`
          }
        }
        this.studentImageUrl = imageUrl
        this.currentStudentScore = this.studentSubmissions[this.currentImageIndex].point
      }
    },

    // 切换到下一张图片
    nextImage() {
      if (this.currentImageIndex < this.studentSubmissions.length - 1) {
        this.currentImageIndex++
        let imageUrl = this.studentSubmissions[this.currentImageIndex].imageUrl
        if (imageUrl && !imageUrl.startsWith('http')) {
          // MinIO文件直接通过MinIO API访问
          if (imageUrl.startsWith('/question-info/')) {
            // MinIO文件路径格式：/bucketName/fileName
            imageUrl = `http://localhost:9000${imageUrl}`
          } else {
            // 其他文件通过本地文件系统访问
            imageUrl = `http://localhost:8080/api/file/preview?path=${encodeURIComponent(imageUrl)}`
          }
        }
        this.studentImageUrl = imageUrl
        this.currentStudentScore = this.studentSubmissions[this.currentImageIndex].point
      }
    },

    // 图片缩放和拖拽相关方法
    handleImageWheel(event) {
      const delta = event.deltaY > 0 ? -0.1 : 0.1
      this.imageScale = Math.max(0.5, Math.min(3, this.imageScale + delta))
    },

    startDrag(event) {
      this.dragging = true
      this.dragStart = { x: event.clientX, y: event.clientY }
      this.lastOffset = { ...this.imageOffset }
      document.addEventListener('mousemove', this.handleDrag)
      document.addEventListener('mouseup', this.stopDrag)
    },

    handleDrag(event) {
      if (!this.dragging) return
      const deltaX = event.clientX - this.dragStart.x
      const deltaY = event.clientY - this.dragStart.y
      this.imageOffset = {
        x: this.lastOffset.x + deltaX,
        y: this.lastOffset.y + deltaY
      }
    },

    stopDrag() {
      this.dragging = false
      document.removeEventListener('mousemove', this.handleDrag)
      document.removeEventListener('mouseup', this.stopDrag)
    },

    // 渲染数学公式
    renderMathFormula(text) {
      if (!text) return ''
      // 这里可以集成数学公式渲染库，如KaTeX或MathJax
      return text
    },

    // 分页相关
    handlePageChange(page) {
      this.currentPage = page
      // 这里可以实现分页逻辑
    },

    // 刷新数据
    refreshData() {
      this.loadData()
    },

    // 返回上一页
    goBack() {
      window.close()
    },

    // 显示上传对话框
    showUploadDialog(student) {
      this.currentUploadStudent = student
      this.uploadDialogVisible = true
      this.uploadFileList = []
    },

    // 显示重新上传对话框
    async showReuploadDialog(student) {
      this.currentReuploadStudent = student
      this.reuploadDialogVisible = true
      this.reuploadFileList = []

      // 获取当前提交的图片
      try {
        const response = await getSubmitByStudentIdAndHomeworkId(student.id, this.homeworkId)
        if (response.code === 1 && response.data && response.data.answerImageUrl) {
          this.currentSubmissionImage = response.data.answerImageUrl
        } else {
          this.currentSubmissionImage = ''
        }
      } catch (error) {
        console.error('获取当前提交图片失败:', error)
        this.currentSubmissionImage = ''
      }
    },

    // 关闭上传对话框
    closeUploadDialog() {
      this.uploadDialogVisible = false
      this.currentUploadStudent = {}
      this.uploadFileList = []
    },

    // 关闭重新上传对话框
    closeReuploadDialog() {
      this.reuploadDialogVisible = false
      this.currentReuploadStudent = {}
      this.currentSubmissionImage = ''
      this.reuploadFileList = []
    },

    // 上传前验证
    beforeUpload(file) {
      const isImage = file.type.startsWith('image/')
      const isLt10M = file.size / 1024 / 1024 < 10

      if (!isImage) {
        this.$message.error('只能上传图片文件!')
        return false
      }
      if (!isLt10M) {
        this.$message.error('图片大小不能超过 10MB!')
        return false
      }
      return true
    },

    // 上传成功处理
    handleUploadSuccess(response, file, fileList) {
      if (response.code === 1) {
        this.$message.success('上传成功!')
        this.uploadFileList = fileList
      } else {
        this.$message.error(response.msg || '上传失败!')
      }
    },

    // 上传失败处理
    handleUploadError(error) {
      this.$message.error('上传失败!')
      console.error('上传失败:', error)
    },

    // 重新上传成功处理
    handleReuploadSuccess(response, file, fileList) {
      if (response.code === 1) {
        this.$message.success('重新上传成功!')
        this.reuploadFileList = fileList
      } else {
        this.$message.error(response.msg || '重新上传失败!')
      }
    },

    // 重新上传失败处理
    handleReuploadError(error) {
      this.$message.error('重新上传失败!')
      console.error('重新上传失败:', error)
    },

    // 确认上传
    async confirmUpload() {
      if (this.uploadFileList.length === 0) {
        this.$message.warning('请先选择要上传的文件!')
        return
      }

      this.uploading = true
      try {
        const formData = new FormData()
        formData.append('file', this.uploadFileList[0].raw)
        formData.append('homeworkId', this.homeworkId)
        formData.append('studentId', this.currentUploadStudent.id)

        const response = await uploadHomeworkImage(formData)
        if (response.code === 1) {
          this.$message.success('作业图片上传成功!')
          this.closeUploadDialog()
          this.refreshData()
        } else {
          this.$message.error(response.msg || '上传失败!')
        }
      } catch (error) {
        this.$message.error('上传失败!')
        console.error('上传失败:', error)
      } finally {
        this.uploading = false
      }
    },

    // 确认重新上传
    async confirmReupload() {
      if (this.reuploadFileList.length === 0) {
        this.$message.warning('请先选择要上传的文件!')
        return
      }

      this.reuploading = true
      try {
        const formData = new FormData()
        formData.append('file', this.reuploadFileList[0].raw)
        formData.append('homeworkId', this.homeworkId)
        formData.append('studentId', this.currentReuploadStudent.id)

        const response = await reuploadHomeworkImage(formData)
        if (response.code === 1) {
          this.$message.success('作业图片重新上传成功!')
          this.closeReuploadDialog()
          this.refreshData()
        } else {
          this.$message.error(response.msg || '重新上传失败!')
        }
      } catch (error) {
        this.$message.error('重新上传失败!')
        console.error('重新上传失败:', error)
      } finally {
        this.reuploading = false
      }
    }
  },

  beforeDestroy() {
    // 清理事件监听器
    document.removeEventListener('mousemove', this.handleDrag)
    document.removeEventListener('mouseup', this.stopDrag)
  }
}
</script>

<style scoped>
.student-submission-list {
  padding: 20px;
  background: #f5f5f5;
  min-height: 100vh;
}

.page-header {
  background: #fff;
  padding: 20px;
  border-radius: 8px;
  margin-bottom: 20px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.page-title {
  margin: 0;
  color: #303133;
  font-size: 24px;
  font-weight: 600;
}

.page-title i {
  margin-right: 10px;
  color: #409EFF;
}

.header-actions {
  display: flex;
  gap: 10px;
}

.page-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.homework-info-card,
.student-list-card {
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.header-filters {
  display: flex;
  align-items: center;
}

.homework-info {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
}

.info-item {
  display: flex;
  align-items: center;
}

.label {
  font-weight: 600;
  color: #606266;
  margin-right: 8px;
}

.value {
  color: #303133;
}

.submitted-count {
  color: #67C23A;
  font-weight: 600;
}

.not-submitted-count {
  color: #F56C6C;
  font-weight: 600;
}

.student-name {
  color: #409EFF;
  cursor: pointer;
  font-weight: 500;
}

.student-name:hover {
  text-decoration: underline;
}

.pagination-container {
  margin-top: 20px;
  display: flex;
  justify-content: center;
}

.correction-dialog-body-beauty {
  display: flex;
  gap: 20px;
  max-height: 70vh;
  overflow: hidden;
}

.correction-image-beauty {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
}

.correction-table-beauty {
  flex: 1;
  overflow-y: auto;
}

.no-image {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: #999;
  border: 2px dashed #ddd;
  border-radius: 8px;
  width: 100%;
}

.zoom-tip {
  margin-top: 10px;
  font-size: 12px;
  color: #999;
}

.image-navigation {
  margin-top: 15px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.image-counter {
  font-size: 14px;
  color: #606266;
}

.student-info h3 {
  margin: 0 0 15px 0;
  color: #303133;
}

.student-answer {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.answer-image {
  margin-bottom: 5px;
}

.answer-text {
  font-size: 14px;
  line-height: 1.4;
}

.no-answer {
  color: #999;
  font-style: italic;
}

.homework-comment-section {
  border-top: 1px solid #e9ecef;
  margin-top: 20px;
  padding-top: 20px;
}
</style>
